import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Smartphone, Building2, Stethoscope, Briefcase, Code, Sparkles, Heart } from 'lucide-react';

const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);
  const [mobilePartnersOpen, setMobilePartnersOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // Handle scroll for navbar backdrop effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const handleBlogClick = () => {
    navigate('/articles');
    setIsOpen(false);
  };

  const closeAllDropdowns = () => {
    setTreatmentsOpen(false);
    setPartnersOpen(false);
  };

  return (
    <nav style={{
      ...styles.nav,
      background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'white',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      boxShadow: scrolled ? '0 4px 24px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={styles.container}>
        <div style={styles.navContent}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_ehealth-portal/artifacts/aqa5vp4n_Frame%201707479730.png" 
              alt="Gewo Health" 
              style={styles.logoImage}
            />
          </div>

          {/* Desktop Menu - Hidden on mobile/tablet */}
          {!isMobile && (
          <div style={styles.desktopMenu}>
            {/* Primary Navigation */}
            <div style={styles.primaryNav}>
              <button 
                onClick={() => { scrollToSection('home'); closeAllDropdowns(); }} 
                style={styles.navLink}
                onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
              >
                Home
              </button>
              
              {/* Services Dropdown (formerly Treatments) */}
              <div 
                style={styles.dropdown}
                onMouseEnter={() => { setTreatmentsOpen(true); setPartnersOpen(false); }}
                onMouseLeave={() => setTreatmentsOpen(false)}
              >
                <button 
                  style={styles.navLink}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
                >
                  Services
                  <ChevronDown size={16} style={{ marginLeft: '4px', transition: 'transform 0.2s', transform: treatmentsOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                {treatmentsOpen && (
                  <div style={styles.dropdownWrapper}>
                    <div 
                      style={{
                        ...styles.dropdownMenu,
                        width: '340px',
                        padding: '16px'
                      }}
                      onMouseEnter={() => setTreatmentsOpen(true)}
                    >
                      {/* Immediate Care Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#1D57D5', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', padding: '0 12px' }}>
                          Immediate Care
                        </div>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/video-consultations'); closeAllDropdowns(); }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#4A4A4A'; }}
                        >
                          🎥 Video Consultations
                        </button>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/find-doctors'); closeAllDropdowns(); }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#4A4A4A'; }}
                        >
                          👨‍⚕️ Find a Doctor
                        </button>
                      </div>

                      {/* Specialist Care Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#28C08A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', padding: '0 12px' }}>
                          Specialist Care
                        </div>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/specializations'); closeAllDropdowns(); }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#4A4A4A'; }}
                        >
                          🏥 All Specializations
                        </button>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/womens-health'); closeAllDropdowns(); }}
                          style={{...styles.dropdownItem, color: '#E91E8C'}}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#FDF2F8'; e.currentTarget.style.color = '#E91E8C'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#E91E8C'; }}
                        >
                          💗 Women's Health
                        </button>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/surgeries'); closeAllDropdowns(); }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#4A4A4A'; }}
                        >
                          🔬 Surgeries & Procedures
                        </button>
                      </div>

                      {/* Health Programs Section */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', padding: '0 12px' }}>
                          Health Programs
                        </div>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/lifestyle-diseases'); closeAllDropdowns(); }}
                          style={{...styles.dropdownItem, color: '#F97316', fontWeight: '600', background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', borderRadius: '8px', marginBottom: '4px'}}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#FFEDD5'; e.currentTarget.style.color = '#EA580C'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)'; e.currentTarget.style.color = '#F97316'; }}
                        >
                          🎯 Lifestyle Disease Management
                        </button>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/mental-health-services'); closeAllDropdowns(); }}
                          style={{...styles.dropdownItem, color: '#205BF3', fontWeight: '600', background: 'linear-gradient(135deg, #F3F7FF 0%, #E8EFFF 100%)', borderRadius: '8px', marginBottom: '4px'}}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EFFF'; e.currentTarget.style.color = '#1A4AC5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #F3F7FF 0%, #E8EFFF 100%)'; e.currentTarget.style.color = '#205BF3'; }}
                        >
                          🧠 Mental Health Services
                        </button>
                        <button 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/geriatric-adult-care'); closeAllDropdowns(); }}
                          style={{...styles.dropdownItem, color: '#1A4AC5', fontWeight: '600', background: 'linear-gradient(135deg, #F6F8FC 0%, #EDF2F7 100%)', borderRadius: '8px', marginBottom: '4px'}}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF2F7'; e.currentTarget.style.color = '#1A4AC5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #F6F8FC 0%, #EDF2F7 100%)'; e.currentTarget.style.color = '#1A4AC5'; }}
                        >
                          🩺 Adult Care – Preventive Health
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => { handleBlogClick(); closeAllDropdowns(); }} 
                style={styles.navLink}
                onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
              >
                Resources
              </button>
              
              <button 
                onClick={() => { navigate('/about'); closeAllDropdowns(); }} 
                style={styles.navLink}
                onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
              >
                About
              </button>
            </div>

            {/* Secondary Navigation */}
            <div style={styles.secondaryNav}>
              {/* Partners Dropdown - Enhanced with Icons & Sectioning */}
              <div 
                style={styles.dropdown}
                onMouseEnter={() => { setPartnersOpen(true); setTreatmentsOpen(false); }}
                onMouseLeave={() => setPartnersOpen(false)}
              >
                <button 
                  style={styles.navLink}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
                >
                  Partners
                  <ChevronDown size={16} style={{ marginLeft: '4px', transition: 'transform 0.2s', transform: partnersOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                {partnersOpen && (
                  <div style={styles.dropdownWrapper}>
                    <div 
                      style={{
                        ...styles.dropdownMenu,
                        width: '380px',
                        padding: '16px'
                      }}
                      onMouseEnter={() => setPartnersOpen(true)}
                    >
                      {/* Healthcare Partners Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#1D57D5', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', padding: '0 12px' }}>
                          🏥 Healthcare Partners
                        </div>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/publish-practice'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Stethoscope size={18} style={{ marginRight: '10px', flexShrink: 0 }} />
                          <span>Healthcare Providers & Clinics</span>
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/hospital-partnerships'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Building2 size={18} style={{ marginRight: '10px', flexShrink: 0 }} />
                          <span>Hospitals & Health Systems</span>
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/organizations'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Briefcase size={18} style={{ marginRight: '10px', flexShrink: 0 }} />
                          <span>Corporate Wellness Programs</span>
                        </button>
                      </div>

                      {/* Technology Partners Section */}
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#9333EA', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', padding: '0 12px' }}>
                          🔧 Technology Partners
                        </div>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/technology-ecosystem'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Heart size={18} style={{ marginRight: '10px', flexShrink: 0 }} fill="#286CFF" />
                          <span>Platform & Technology Overview</span>
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/api-partners'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Code size={18} style={{ marginRight: '10px', flexShrink: 0 }} />
                          <span>API Integration & Developers</span>
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/innovation-partnership'); 
                            closeAllDropdowns(); 
                          }}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F4FF';
                            e.currentTarget.style.color = '#286CFF';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#4A4A4A';
                          }}
                        >
                          <Sparkles size={18} style={{ marginRight: '10px', flexShrink: 0 }} />
                          <span>Digital Health Innovators</span>
                        </button>
                      </div>

                      {/* View All Link */}
                      <div style={{ borderTop: '1px solid #E8ECF0', marginTop: '12px', paddingTop: '12px' }}>
                        <button 
                          onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/partners'); 
                            closeAllDropdowns(); 
                          }}
                          style={{
                            ...styles.dropdownItem,
                            color: '#1D57D5',
                            fontWeight: '600',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)';
                            e.currentTarget.style.color = '#1D57D5';
                            e.currentTarget.style.cursor = 'pointer';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#1D57D5';
                          }}
                        >
                          View All Partnership Programs →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => { scrollToSection('app'); closeAllDropdowns(); }} 
                style={styles.downloadAppLink}
                onMouseEnter={(e) => e.currentTarget.style.color = '#286CFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A4A4A'}
              >
                <Smartphone size={18} style={{ marginRight: '6px' }} />
                Download App
              </button>
              
              <button 
                style={styles.btnConsult}
                onClick={() => { navigate('/specializations'); closeAllDropdowns(); }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FFD28A';
                  e.currentTarget.style.color = '#4A4A4A';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#286CFF';
                  e.currentTarget.style.color = 'white';
                }}
              >
                Consult Now
              </button>
            </div>
          </div>
          )}

          {/* Mobile Menu Button - Only show on mobile/tablet */}
          {isMobile && (
          <button style={{...styles.mobileMenuBtn, display: 'block'}} onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          )}
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuContent}>
              <button onClick={() => scrollToSection('home')} style={styles.mobileNavLink}>
                Home
              </button>
              
              {/* Services Dropdown Mobile (formerly Treatments) */}
              <div style={styles.mobileDropdown}>
                <button 
                  onClick={() => setMobileTreatmentsOpen(!mobileTreatmentsOpen)} 
                  style={styles.mobileNavLinkWithIcon}
                >
                  Services
                  <ChevronDown size={18} style={{ transition: 'transform 0.2s', transform: mobileTreatmentsOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                {mobileTreatmentsOpen && (
                  <div style={styles.mobileSubmenu}>
                    {/* Immediate Care */}
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#1D57D5', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px 6px', borderTop: '1px solid #E5E7EB' }}>
                      Immediate Care
                    </div>
                    <button 
                      onClick={() => { navigate('/video-consultations'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#718096'; }}
                    >
                      🎥 Video Consultations
                    </button>
                    <button 
                      onClick={() => { navigate('/find-doctors'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#718096'; }}
                    >
                      👨‍⚕️ Find a Doctor
                    </button>

                    {/* Specialist Care */}
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#28C08A', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px 6px', borderTop: '1px solid #E5E7EB', marginTop: '8px' }}>
                      Specialist Care
                    </div>
                    <button 
                      onClick={() => { navigate('/specializations'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#718096'; }}
                    >
                      🏥 All Specializations
                    </button>
                    <button 
                      onClick={() => { navigate('/womens-health'); setIsOpen(false); }} 
                      style={{...styles.mobileSubmenuItem, color: '#E91E8C', fontWeight: '600'}}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#FDF2F8'; e.currentTarget.style.color = '#E91E8C'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#E91E8C'; }}
                    >
                      💗 Women's Health
                    </button>
                    <button 
                      onClick={() => { navigate('/surgeries'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FF'; e.currentTarget.style.color = '#286CFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#718096'; }}
                    >
                      🔬 Surgeries & Procedures
                    </button>

                    {/* Health Programs */}
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px 6px', borderTop: '1px solid #E5E7EB', marginTop: '8px' }}>
                      Health Programs
                    </div>
                    <button 
                      onClick={() => { navigate('/lifestyle-diseases'); setIsOpen(false); }} 
                      style={{...styles.mobileSubmenuItem, color: '#F97316', fontWeight: '700', background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', borderRadius: '8px', margin: '4px 8px'}}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#FFEDD5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)'; }}
                    >
                      🎯 Lifestyle Disease Management
                    </button>
                    <button 
                      onClick={() => { navigate('/mental-health-services'); setIsOpen(false); }} 
                      style={{...styles.mobileSubmenuItem, color: '#205BF3', fontWeight: '700', background: 'linear-gradient(135deg, #F3F7FF 0%, #E8EFFF 100%)', borderRadius: '8px', margin: '4px 8px'}}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EFFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #F3F7FF 0%, #E8EFFF 100%)'; }}
                    >
                      🧠 Mental Health Services
                    </button>
                    <button 
                      onClick={() => { navigate('/geriatric-adult-care'); setIsOpen(false); }} 
                      style={{...styles.mobileSubmenuItem, color: '#1A4AC5', fontWeight: '700', background: 'linear-gradient(135deg, #F6F8FC 0%, #EDF2F7 100%)', borderRadius: '8px', margin: '4px 8px'}}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF2F7'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #F6F8FC 0%, #EDF2F7 100%)'; }}
                    >
                      🩺 Adult Care – Preventive Health
                    </button>
                  </div>
                )}
              </div>
              
              <button onClick={handleBlogClick} style={styles.mobileNavLink}>
                Resources
              </button>
              
              <button onClick={() => { navigate('/about'); setIsOpen(false); }} style={styles.mobileNavLink}>
                About
              </button>
              
              {/* Partners Dropdown Mobile */}
              <div style={styles.mobileDropdown}>
                <button 
                  onClick={() => setMobilePartnersOpen(!mobilePartnersOpen)} 
                  style={styles.mobileNavLinkWithIcon}
                >
                  Partners
                  <ChevronDown size={18} style={{ transition: 'transform 0.2s', transform: mobilePartnersOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                {mobilePartnersOpen && (
                  <div style={styles.mobileSubmenu}>
                    {/* Healthcare Partners */}
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#1D57D5', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px 6px', borderTop: '1px solid #E5E7EB' }}>
                      🏥 Healthcare Partners
                    </div>
                    <button 
                      onClick={() => { navigate('/publish-practice'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      Healthcare Providers & Clinics
                    </button>
                    <button 
                      onClick={() => { navigate('/hospital-partnerships'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      Hospitals & Health Systems
                    </button>
                    <button 
                      onClick={() => { navigate('/organizations'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      Corporate Wellness Programs
                    </button>

                    {/* Technology Partners */}
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#9333EA', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px 6px', borderTop: '1px solid #E5E7EB', marginTop: '8px' }}>
                      🔧 Technology Partners
                    </div>
                    <button 
                      onClick={() => { navigate('/technology-ecosystem'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      Platform & Technology Overview
                    </button>
                    <button 
                      onClick={() => { navigate('/api-partners'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      API Integration & Developers
                    </button>
                    <button 
                      onClick={() => { navigate('/innovation-partnership'); setIsOpen(false); }} 
                      style={styles.mobileSubmenuItem}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F0F4FF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      Digital Health Innovators
                    </button>

                    {/* View All Link */}
                    <button 
                      onClick={() => { navigate('/partners'); setIsOpen(false); }} 
                      style={{...styles.mobileSubmenuItem, color: '#1D57D5', fontWeight: '700', background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)', borderRadius: '8px', margin: '8px 8px 4px'}}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#E3EDFF'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)'; }}
                    >
                      View All Partnership Programs →
                    </button>
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => scrollToSection('app')} 
                style={styles.mobileDownloadLink}
              >
                <Smartphone size={18} style={{ marginRight: '6px' }} />
                Download App
              </button>
            </div>
            
            {/* Sticky Consult Now Button at Bottom */}
            <div style={styles.mobileCtaContainer}>
              <button 
                style={styles.btnPrimaryMobile}
                onClick={() => { navigate('/specializations'); setIsOpen(false); }}
              >
                Consult Now
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const styles = {
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid #E8ECF0',
    zIndex: 1000,
    padding: '12px 0',
    marginBottom: '10px',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  navContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  logoImage: {
    height: '45px',
    width: 'auto'
  },
  desktopMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: '40px',
    flex: 1,
    justifyContent: 'space-between',
    marginLeft: '60px'
  },
  primaryNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px'
  },
  secondaryNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px'
  },
  navLink: {
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    padding: '8px 0',
    display: 'flex',
    alignItems: 'center'
  },
  downloadAppLink: {
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    padding: '8px 0',
    display: 'flex',
    alignItems: 'center'
  },
  dropdown: {
    position: 'relative',
    display: 'inline-block',
    cursor: 'pointer'
  },
  dropdownWrapper: {
    position: 'absolute',
    top: '100%',
    left: '0',
    paddingTop: '8px',
    zIndex: 1001
  },
  dropdownMenu: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid #E8ECF0',
    minWidth: '200px',
    padding: '8px',
    pointerEvents: 'auto'
  },
  dropdownItem: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '12px 16px',
    textAlign: 'left',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'auto',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  },
  btnConsult: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '28px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '44px',
    boxShadow: '0 2px 8px rgba(40, 108, 255, 0.3)'
  },
  btnSecondary: {
    background: 'transparent',
    color: '#286CFF',
    border: '2px solid #286CFF',
    borderRadius: '28px',
    padding: '10px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '44px'
  },
  mobileMenuBtn: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#4A4A4A'
  },
  mobileMenu: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '24px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 100px)',
    position: 'relative'
  },
  mobileMenuContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '16px',
    overflowY: 'auto',
    flex: 1
  },
  mobileNavLink: {
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '14px 12px',
    textAlign: 'left',
    borderRadius: '8px',
    transition: 'background 0.2s ease'
  },
  mobileNavLinkWithIcon: {
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '14px 12px',
    textAlign: 'left',
    borderRadius: '8px',
    transition: 'background 0.2s ease',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  mobileDropdown: {
    width: '100%'
  },
  mobileSubmenu: {
    paddingLeft: '16px',
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  mobileSubmenuItem: {
    background: 'none',
    border: 'none',
    color: '#718096',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '12px 12px',
    textAlign: 'left',
    borderRadius: '8px',
    transition: 'all 0.2s ease'
  },
  mobileDownloadLink: {
    background: 'none',
    border: 'none',
    color: '#4A4A4A',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '14px 12px',
    textAlign: 'left',
    borderRadius: '8px',
    transition: 'background 0.2s ease',
    display: 'flex',
    alignItems: 'center'
  },
  mobileCtaContainer: {
    padding: '16px',
    borderTop: '1px solid #E8ECF0',
    background: '#F7FAFC',
    position: 'sticky',
    bottom: 0
  },
  btnPrimaryMobile: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '28px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.3)'
  },
  btnSecondaryMobile: {
    background: 'transparent',
    color: '#286CFF',
    border: '2px solid #286CFF',
    borderRadius: '28px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px'
  }
};

export default Navbar;